import type { LucideIcon } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePageTitle } from '@/hooks/usePageTitle';

export interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  /** Pastille d'icône standardisée (dégradé accent unique pour toute l'app). */
  icon?: LucideIcon;
  /** Remplace la pastille : logo, avatar (fiche transporteur, fiche d'AO…). */
  leading?: React.ReactNode;
  /** À droite du titre, sur la même ligne : badges de statut, compteurs. */
  meta?: React.ReactNode;
  /** Actions à droite (boutons) ; passent à la ligne sur mobile. */
  actions?: React.ReactNode;
  /**
   * Retour vers la page d'origine. `to` pour un lien, `onClick` quand le
   * retour dépend de l'historique ou du contexte (voir `lib/return-url.ts`).
   */
  backTo?: { to: string; label: string } | { onClick: () => void; label: string };
  /** Rangée d'onglets sous l'en-tête, avec son filet. */
  tabs?: React.ReactNode;
  /**
   * Titre de l'onglet du navigateur. Par défaut le `title` de la page ;
   * `false` quand un parent le gère déjà (Réglages).
   */
  documentTitle?: string | false;
  className?: string;
}

/**
 * En-tête de page standard — un seul gabarit pour toutes les pages : taille de
 * titre, pastille d'icône, retour, actions et titre d'onglet uniformes, avec
 * repli correct en mobile. Remplace les en-têtes ad hoc relevés en audit
 * (5 tailles de h1, 3 dégradés, retours sans libellé).
 */
export function PageHeader({
  title,
  description,
  icon: Icon,
  leading,
  meta,
  actions,
  backTo,
  tabs,
  documentTitle,
  className,
}: PageHeaderProps) {
  usePageTitle(documentTitle === false ? '' : (documentTitle ?? title));

  return (
    <div className={cn('mb-5', className)}>
      {backTo && (
        <BackLink backTo={backTo} />
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {leading ??
            (Icon && (
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded bg-gradient-to-br from-accent to-accent-hover shadow-sm">
                <Icon className="h-[18px] w-[18px] text-white" aria-hidden="true" />
              </div>
            ))}
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
              <h1 className="min-w-0 truncate text-xl font-semibold text-text">{title}</h1>
              {meta}
            </div>
            {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {tabs && <div className="mt-4">{tabs}</div>}
    </div>
  );
}

function BackLink({ backTo }: { backTo: NonNullable<PageHeaderProps['backTo']> }) {
  const className =
    'mb-2 inline-flex items-center gap-1 text-[13px] text-muted transition-colors hover:text-text';
  const content = (
    <>
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      {backTo.label}
    </>
  );
  if ('to' in backTo) {
    return (
      <Link to={backTo.to} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={backTo.onClick} className={className}>
      {content}
    </button>
  );
}
